import { ContentApi } from '@/api/modules/content';

/**
 * Supported file types for the upload system
 */
export const SUPPORTED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'video/mp4', 'video/quicktime'];

/**
 * File upload utility for Flashy
 * Handles the entire process from getting presigned URLs to adding content to database
 * Follows the exact same pattern as UploadImagesDialog
 */
const fileUpload = {
  /**
   * Upload a single file and add it to content
   * @param {File} file - The file to upload
   * @param {Function} progressCallback
   * @returns {Promise<Object>} - The newly added content object
   */
  uploadFile: async file => {
    if (!file) {
      throw new Error('No file provided');
    }

    // Step 1: Prepare file data
    const fileData = await fileUpload.prepareFileData(file);

    // Step 2: Get presigned URLs
    const response = await fileUpload.getPresignedUrl(fileData);

    // Step 3: Upload to storage
    await fileUpload.uploadToStorage(response.data.presignedUrls[0].presignedUrl, fileData);

    // Step 4: Create database entry
    return await fileUpload.createContentEntry(response.data.contentMetadata);
  },

  /**
   * Prepare file data with metadata
   * @param {File} file - The file to process
   * @param {Function} progressCallback
   * @returns {Promise<Object>} - File data object
   */
  prepareFileData: async file => {
    // Get dimensions and other metadata
    const metadata = await fileUpload.getMediaMetadata(file);

    // Return structured file data
    return {
      name: file.name,
      relativePath: file.name,
      contentType: file.type,
      isVideo: file.type.startsWith('video/'),
      aspectRatio: parseFloat((metadata.width / metadata.height).toFixed(3)),
      duration: file.type.startsWith('video/') ? metadata.duration : 0,
      file: file
    };
  },

  /**
   * Get presigned URL from API
   * @param {Object} fileData - Prepared file data
   * @param {Function} progressCallback
   * @returns {Promise<Object>} - API response with presigned URLs
   */
  getPresignedUrl: async fileData => {
    const shootId = 0; // Non-shoot-related content
    return await ContentApi.getPresignedUrls({
      id: shootId,
      files: [
        {
          name: fileData.name,
          relativePath: fileData.relativePath,
          contentType: fileData.contentType,
          aspectRatio: fileData.aspectRatio,
          duration: fileData.duration
        }
      ]
    });
  },

  /**
   * Upload file to storage using presigned URL
   * @param {string} presignedUrl - The URL to upload to
   * @param {Object} fileData - File data object
   * @param {Function} progressCallback
   * @returns {Promise<void>}
   */
  uploadToStorage: async (presignedUrl, fileData) => {
    return await fileUpload.uploadSingleFile(presignedUrl, fileData, 0, 1);
  },

  /**
   * Create content entry in database
   * @param {Object} contentMetadata - Metadata for content creation
   * @param {Function} progressCallback
   * @returns {Promise<Object>} - The created content object
   */
  createContentEntry: async contentMetadata => {
    const shootId = 0;
    const batchResponse = await ContentApi.addContent({
      id: shootId,
      payload: {
        content: contentMetadata
      }
    });

    // Return the newly added content (first item in the response)
    const contentData = batchResponse.data.content || batchResponse.data.images;
    return contentData[0];
  },

  /**
   * Get dimensions and metadata from media file
   * This exactly mirrors the implementation in UploadImagesDialog
   * @param {File} file - The file to process
   * @returns {Promise<Object>} - Object with width, height, and duration
   */
  getMediaMetadata: file => {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);

      if (file.type.startsWith('video/')) {
        // Handle video files
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
          URL.revokeObjectURL(objectUrl);
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: Math.round(video.duration)
          });
        };

        video.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Failed to load video metadata'));
        };

        video.src = objectUrl;
      } else if (file.type.startsWith('image/')) {
        // Handle image files
        const img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve({
            width: img.width,
            height: img.height,
            duration: 0
          });
        };

        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Failed to load image'));
        };

        img.src = objectUrl;
      } else {
        // Default for other file types
        URL.revokeObjectURL(objectUrl);
        resolve({
          width: 0,
          height: 0,
          duration: 0
        });
      }
    });
  },

  /**
   * Upload a single file to storage
   * @param {string} presignedUrl - The presigned URL to upload to
   * @param {Object} fileData - The file data object with file and metadata
   * @param {number} index - The index of the current file in batch
   * @param {number} total - The total number of files in batch
   * @returns {Promise} - Resolves when upload completes
   */
  uploadSingleFile: (presignedUrl, fileData) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', fileData.contentType);

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      xhr.send(fileData.file);
    });
  },

  /**
   * Generate video thumbnail from video file
   * @param {File} videoFile - The video file to generate thumbnail from
   * @returns {Promise<string>} - Promise that resolves with thumbnail blob URL
   */
  generateVideoThumbnail: videoFile => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.preload = 'metadata';
      video.muted = true; // Required for some browsers

      video.onloadedmetadata = () => {
        // Set canvas size to video dimensions (with max size limit)
        const maxSize = 200; // Max thumbnail size
        const aspectRatio = video.videoWidth / video.videoHeight;

        if (video.videoWidth > video.videoHeight) {
          canvas.width = Math.min(maxSize, video.videoWidth);
          canvas.height = canvas.width / aspectRatio;
        } else {
          canvas.height = Math.min(maxSize, video.videoHeight);
          canvas.width = canvas.height * aspectRatio;
        }

        // Seek to 1 second or 10% of video duration, whichever is smaller
        const seekTime = Math.min(1, video.duration * 0.1);
        video.currentTime = seekTime;
      };

      video.onseeked = () => {
        try {
          // Draw video frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Convert canvas to blob
          canvas.toBlob(
            blob => {
              if (blob) {
                const thumbnailUrl = URL.createObjectURL(blob);
                resolve(thumbnailUrl);
              } else {
                reject(new Error('Failed to create thumbnail blob'));
              }
            },
            'image/jpeg',
            0.8
          ); // 80% quality JPEG

          // Clean up
          URL.revokeObjectURL(video.src);
        } catch (error) {
          reject(error);
        }
      };

      video.onerror = () => {
        reject(new Error('Failed to load video for thumbnail generation'));
      };

      // Load the video file
      video.src = URL.createObjectURL(videoFile);
    });
  },

  /**
   * Generate video thumbnail asynchronously (non-blocking)
   * @param {File} videoFile - The video file to generate thumbnail from
   * @returns {Promise<string>} - Promise that resolves with thumbnail blob URL
   */
  generateVideoThumbnailAsync: async videoFile => {
    return fileUpload.generateVideoThumbnail(videoFile);
  }
};

export default fileUpload;
